/**
 * The panels that are deliberately NOT recharts, and the sub-section furniture
 * the hub panels share.
 *
 * Two of them are not charts on purpose and the reasons are recorded where they
 * are used:
 *
 *   · THE RANKED BAR LISTS. A recharts `<Bar>` of height zero is not drawn at
 *     all, and the zero row — "which of my sites has gone quiet" — is the most
 *     valuable row on the panel. Every row here draws its track whether or not
 *     it has a value, the fill carries a 3px minimum stub, and the figure is
 *     printed beside the label rather than inferred from the bar's length.
 *   · THE RHYTHM HEATMAP. 168 `<Cell>` elements inside a recharts scatter mount
 *     168 SVG nodes through a pipeline whose whole job is computing scales this
 *     grid does not have — both axes are fixed positions known before any data
 *     arrives. Worse, three things the panel needs are awkward or impossible
 *     inside the chart: a visible boundary on an empty cell, a real focus target
 *     per cell, and a per-cell accessible name. A CSS grid gives all three and
 *     costs 168 divs. `dashboard-screen/chart-sizing.test.ts` asserts it stays a grid.
 */
import styled from '@emotion/styled';
import {
  layout,
  palette,
  radius,
  shadow,
  space,
  spacing,
  transition,
  typography,
  zIndex,
  status,
} from '../../../../../style-utils/theme';
import { dataViz } from '../../../../../style-utils/theme';
import { touch } from '../../../../../style-utils/media-queries';

/* ───────────────────────────  ranked bar list  ────────────────────────── */

export const BarList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
`;

export const BarRowItem = styled.li`
  min-width: 0;
`;

export const BarRow = styled.div<{ interactive?: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas:
    'label value'
    'track track';
  gap: ${spacing.half} ${spacing.xs};
  width: 100%;
  padding: ${spacing.xs};
  text-align: left;
  font: inherit;
  background: none;
  border: 1px solid transparent;
  border-radius: ${radius.xs};
  cursor: ${({ interactive }) => (interactive ? 'pointer' : 'default')};
  transition: ${transition.colors};

  ${({ interactive }) =>
    interactive
      ? `
    &:hover { background: ${palette.irisTintSoft}; border-color: ${palette.line}; }
    &:focus-visible { outline: none; box-shadow: ${shadow.focus}; }
  `
      : ''}

  ${touch} {
    min-height: ${layout.touchTarget};
  }
`;

export const BarLabel = styled.span`
  grid-area: label;
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

export const BarValueGroup = styled.span`
  grid-area: value;
  display: inline-flex;
  align-items: baseline;
  gap: ${spacing.half};
  white-space: nowrap;
`;

export const BarValue = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.bold};
  font-variant-numeric: tabular-nums;
  color: ${palette.ink};
`;

export const BarTrack = styled.span`
  grid-area: track;
  position: relative;
  display: block;
  width: 100%;
  height: 10px;
  border-radius: ${radius.pill};
  background: ${palette.surfaceMuted};
  overflow: hidden;
`;

/** A 3px MINIMUM STUB, so a zero reads as a deliberate zero rather than as an
 *  absent row. This is the whole reason these panels are not recharts. */
export const BarFill = styled.span<{ pct: number; tone?: string }>`
  position: absolute;
  inset: 0 auto 0 0;
  width: ${({ pct }) => `max(3px, ${Math.max(0, Math.min(100, pct))}%)`};
  border-radius: ${radius.pill};
  background: ${({ tone }) => tone || dataViz.series[0]};
`;

/** The previous period as a 2px MARKER on the same track, never a second bar.
 *  The reader compares to it once; a second bar doubles the ink for a single
 *  comparison and turns twelve rows into twenty-four marks. */
export const BarMarker = styled.span<{ pct: number }>`
  position: absolute;
  top: -2px;
  bottom: -2px;
  left: ${({ pct }) => `${Math.max(0, Math.min(100, pct))}%`};
  width: 2px;
  background: ${palette.inkSoft};
`;

/* ─────────────────────────  figures (percentiles)  ────────────────────── */

/** THREE FIGURES, NOT ONE. p50, p90 and p99 answer three different questions and
 *  collapsing them into a headline is what a mean does.
 *
 *  auto-fit on the GRID'S OWN WIDTH, not a viewport breakpoint — the RingRow
 *  reasoning. This was three columns from `sm` up whatever the panel was given,
 *  so at 1280 with the filter rail open the quality headline's three cards
 *  were 75px each and their notes ran to fifteen lines of ten letters. A card
 *  now keeps FIGURE_MIN and the row reflows to two, then one; min(100%, …)
 *  lets a panel narrower than one card still hold one without overflowing. */
const FIGURE_MIN = space(20);

export const FigureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, ${FIGURE_MIN}), 1fr));
  gap: ${spacing.xs};
`;

export const FigureCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
  padding: ${spacing.xs};
  background: ${palette.surfaceMuted};
  border-radius: ${radius.sm};
`;

export const FigureLabel = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.slate};
`;

export const FigureValue = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xl}px;
  font-weight: ${typography.weight.bold};
  font-variant-numeric: tabular-nums;
  line-height: ${typography.leading.tight};
  color: ${palette.ink};
`;

export const FigureUnit = styled.span`
  margin-left: 4px;
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.regular};
  color: ${palette.slateLight};
`;

export const FigureNote = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

/* ──────────────────────────────  meters  ─────────────────────────────── */

/** METERS, NOT GAUGES, and the reason is pixels per fact: a semicircular gauge
 *  spends roughly four times the area on the same one number, and it spends it
 *  on an arc where 67% and 74% land a few degrees apart — precisely the pair a
 *  manager needs to tell apart. Six straight tracks share one baseline down the
 *  column, so the six ratios are comparable by eye before any is read. */
export const MeterList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
`;

export const MeterItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
`;

export const MeterHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${spacing.xs};
  flex-wrap: wrap;
`;

export const MeterName = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

export const MeterValue = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.bold};
  font-variant-numeric: tabular-nums;
  color: ${palette.ink};
`;

/** THE RAW PAIR travels beside every ratio: a percentage without its denominator
 *  is a percentage nobody can size — 67% of three entries and 67% of three
 *  thousand are the same number and different evidence. */
export const MeterRaw = styled.span`
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.regular};
  color: ${palette.slateLight};
`;

export const MeterTrack = styled.div`
  position: relative;
  width: 100%;
  height: 8px;
  border-radius: ${radius.pill};
  background: ${palette.surfaceMuted};
  overflow: hidden;
`;

export const MeterFill = styled.span<{ pct: number }>`
  position: absolute;
  inset: 0 auto 0 0;
  width: ${({ pct }) => `${Math.max(0, Math.min(100, pct))}%`};
  background: ${dataViz.series[0]};
  border-radius: ${radius.pill};
`;

/** NULL IS NOT ZERO. An empty solid track is the same picture as 0%, and 0% is a
 *  claim this data does not make — the server sends null for a zero denominator
 *  on purpose. A dashed hollow track is the absence. */
export const MeterAbsent = styled.div`
  width: 100%;
  height: 8px;
  border: 1px dashed ${palette.lineStrong};
  border-radius: ${radius.pill};
  background: transparent;
`;

export const MeterMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
  flex-wrap: wrap;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};

  strong {
    color: ${palette.ink};
  }
  > span + span::before {
    content: ' · ';
    color: ${palette.slateLight};
  }
`;

export const StatePill = styled.span<{ tone?: 'warning' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  padding: 0 ${spacing.half};
  border-radius: ${radius.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  /* status.warning.fg, NOT palette.warning. Amber text on the amber wash is
     1.92:1 — the worst pair the audit measured anywhere — and the status
     triples exist precisely so a tint always carries a label that can be read
     on it. Only visible once a screen has data: an empty analytics panel never
     renders this pill. */
  color: ${({ tone }) => (tone === 'warning' ? status.warning.fg : palette.slate)};
  background: ${({ tone }) =>
    tone === 'warning' ? palette.warningSoft : palette.surfaceMuted};
  white-space: nowrap;
`;

export const DrillRegion = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  padding: ${spacing.xs};
  background: ${palette.surfaceMuted};
  border-radius: ${radius.sm};
`;

/* ───────────────────────────  panel sections  ─────────────────────────── */

export const PanelSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  min-width: 0;
  padding-top: ${spacing.xs};
  border-top: 1px solid ${palette.line};

  &:first-of-type {
    border-top: none;
    padding-top: 0;
  }
`;

export const PanelSectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.xs};
  flex-wrap: wrap;
`;

export const PanelSectionTitle = styled.h4`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.bold};
  color: ${palette.ink};
`;

export const SectionCaption = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};

  strong {
    color: ${palette.ink};
  }
`;

export const LiveTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slateLight};
  white-space: nowrap;
`;

export const LiveDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: ${radius.circle};
  background: ${palette.iris};
`;

/* ──────────────────────────  occupancy stacks  ────────────────────────── */

export const StackList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

export const StackItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
`;

export const StackTrack = styled.div`
  display: flex;
  width: 100%;
  height: 12px;
  border-radius: ${radius.pill};
  overflow: hidden;
  background: ${palette.surfaceMuted};
`;

export const StackSeg = styled.span<{ pct: number; tone: string }>`
  width: ${({ pct }) => `${Math.max(0, Math.min(100, pct))}%`};
  background: ${({ tone }) => tone};
`;

/** A HATCH, NOT A SOLID FILL. "Unrecorded" is the ABSENCE of an answer, not a
 *  third kind of tenancy, and painting it solid is exactly how an occupancy rate
 *  becomes fiction. */
export const StackSegUnrecorded = styled.span<{ pct: number }>`
  width: ${({ pct }) => `${Math.max(0, Math.min(100, pct))}%`};
  background-image: repeating-linear-gradient(
    45deg,
    ${palette.surfaceMuted},
    ${palette.surfaceMuted} 3px,
    ${palette.lineStrong} 3px,
    ${palette.lineStrong} 6px
  );
`;

export const KeyRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};
  align-items: center;
`;

export const KeyItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slate};
`;

export const KeySwatch = styled.span<{ tone: string }>`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: ${({ tone }) => tone};
  flex: 0 0 auto;
`;

export const KeySwatchHatched = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  flex: 0 0 auto;
  background-image: repeating-linear-gradient(
    45deg,
    ${palette.surfaceMuted},
    ${palette.surfaceMuted} 3px,
    ${palette.lineStrong} 3px,
    ${palette.lineStrong} 6px
  );
`;

/* ─────────────────────────────  heat grid  ───────────────────────────── */

/** 24 columns at a legible size cannot fit 375px, and the choice is a scrollbar
 *  inside the panel or a horizontal scrollbar on the whole application. */
export const HeatWrap = styled.div`
  position: relative;
  min-width: 640px;
`;

export const HeatGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};

  &:focus-visible {
    outline: none;
  }
`;

export const HeatRow = styled.div`
  display: grid;
  /* minmax(0, 1fr), NEVER a bare 1fr: a bare track has a min-content floor and
     bleeds the panel sideways. */
  grid-template-columns: 56px repeat(24, minmax(0, 1fr));
  gap: ${spacing.half};
  align-items: center;
`;

export const HeatCorner = styled.span``;

export const HeatHourLabel = styled.span`
  font-family: ${typography.ui};
  font-size: 10px;
  color: ${palette.slateLight};
  text-align: center;
  white-space: nowrap;
`;

export const HeatDayLabel = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slate};
  white-space: nowrap;
`;

/** An empty cell is the palest step WITH a border, because at 1.10:1 against the
 *  surface the fill alone is not a mark — and a dead hour being VISIBLE is the
 *  entire value of this panel. An omitted cell renders as a hole, and a hole
 *  reads as missing data rather than as "nobody logged anything then". */
export const HeatCell = styled.div<{ tone: string; peak?: boolean }>`
  height: 20px;
  border-radius: 2px;
  background: ${({ tone }) => tone};
  border: 1px solid
    ${({ peak }) => (peak ? palette.ink : 'rgba(18, 41, 44, 0.06)')};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${palette.iris};
    outline-offset: 1px;
    z-index: ${zIndex.raised};
  }

  /* Under a thumb the whole grid grows rather than the cells becoming taps that
     miss. 168 targets is not a control surface, and the readout is reachable by
     tapping a cell of this size at this scale. */
  ${touch} {
    height: 28px;
  }
`;

export const HeatTip = styled.div`
  position: absolute;
  z-index: ${zIndex.raised};
  transform: translate(-50%, -100%);
  pointer-events: none;
`;

export const HeatLegend = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
`;

/** A GRADIENT, NOT SWATCHES. `dataViz.sequential` is validated as a CONTINUOUS
 *  FILL: its first step is ΔL 0.051 from the second against a 0.06 floor, and
 *  its palest step is 1.10:1 against the surface. Seven chips built from it
 *  would show two steps nobody can separate and one nobody can see.
 *  `chart-sizing.test.ts` asserts this stays a gradient. */
export const HeatLegendBar = styled.div`
  flex: 1 1 auto;
  height: 10px;
  border-radius: ${radius.pill};
  background: linear-gradient(to right, ${dataViz.sequential.join(', ')});
`;

export const HeatLegendLabel = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slateLight};
  white-space: nowrap;
`;

/* TWO RINGS, SIDE BY SIDE ON A DESK AND STACKED ON A PHONE. auto-fit rather
   than a media query: the panel is placed in a grid whose column count already
   varies, so the ring row has to respond to ITS OWN width rather than to the
   viewport's. */
export const RingRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${spacing.sm};
  align-items: start;
`;

/* ───────────────────────────  small multiples  ───────────────────────── */

/* auto-fill, not auto-fit: with three tiles on a wide desk, auto-fit stretches
   them to full width and the comparison the panel exists for gets harder the
   MORE room there is. auto-fill keeps the tile size honest and leaves the gap. */
export const MultiplesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: ${spacing.sm};
`;

export const MultiplesTile = styled.button`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
  padding: ${spacing.xs};
  text-align: left;
  font: inherit;
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  cursor: pointer;
  transition: ${transition.colors};

  &:hover {
    border-color: ${palette.lagoon};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const MultiplesTitle = styled.span`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${spacing.half};
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slate};

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  strong {
    flex: none;
    font-variant-numeric: tabular-nums;
    color: ${palette.ink};
  }
`;

export const PanelToggleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing.xs};
  min-width: 0;
`;
